import type { Dialability } from '../../../services/dialerApi'
import { countryName } from '../../utils/phone'

export function callsUnavailableMessage(country: string | null | undefined): string {
  const where = country ? countryName(country) : 'this contact’s country'
  return `Calls to ${where} aren’t available yet.`
}

/** May the session dial this on its own?
 *
 * Deliberately stricter than {@link canOneOffDial}: a 'cooldown' line was called
 * minutes ago, and cascading onto it would re-dial someone we just rang. A rep
 * who decides otherwise can still place the call by hand.
 */
export function canAutoDial(d: Dialability): boolean {
  switch (d) {
    case 'ready':
      return true
    case 'cooldown':
    case 'blocked_no_local_number':
      return false
  }
  // A new Dialability without a decision here fails to compile.
  const unhandled: never = d
  return unhandled
}

/** May a rep dial this by hand?
 *
 * Looser than {@link canAutoDial} on purpose — the recency cooldown is guidance,
 * and overriding it is the point of a one-off. Only a missing local number is a
 * hard no, because there is nothing to dial from.
 */
export function canOneOffDial(d: Dialability): boolean {
  switch (d) {
    case 'ready':
    case 'cooldown':
      return true
    case 'blocked_no_local_number':
      return false
  }
  const unhandled: never = d
  return unhandled
}

/** Which rail section a row belongs in. Total over Dialability × label, so a new
 *  variant cannot make rows silently vanish from every section. */
export function railSection(
  d: Dialability,
  label: string,
): 'warm' | 'cold' | 'cooldown' | 'noNumber' {
  switch (d) {
    case 'ready':
      return label === 'cold' ? 'cold' : 'warm'
    case 'cooldown':
      return 'cooldown'
    case 'blocked_no_local_number':
      return 'noNumber'
  }
  const unhandled: never = d
  return unhandled
}
